import { useCallback, useEffect, useState } from 'react';
import { listenToCompanyFact, listenToCounterpartiesFact } from '../../services/advisoryService';
import type { CompanyFact, CounterpartiesFact } from '../../redux/slices/advisory/advisoryTypes';

/**
 * The confirmed company and counterparties for one project, from
 * `facts/company` and `facts/counterparties`.
 *
 * BOTH are live listeners, not one-shot reads. The company document does change
 * only twice — when the name is read out of the user's question and when the
 * user confirms it — and both writes land on a turn this client is already
 * streaming. The counterparties document does not: its `profiles` map gains an
 * entry per company as agent-svc's research moves that company from
 * `researching` to `ingested` or to one of the failure statuses, and its
 * `dropped` list grows on the same schedule. That research runs 5-20 minutes per
 * company in a detached Cloud Run Job that outlives the tab, so on a reload
 * there is no stream behind those writes at all.
 *
 * A one-shot read is therefore not merely stale, it is permanently stale: reload
 * mid-research and the screen keeps reporting `A: ingested, B: researching,
 * C: queued` for as long as it is open, with a Retry that never becomes
 * relevant and a Continue that never appears because `pending` never clears.
 *
 * `undefined` while the first snapshot is outstanding and `null` when there is
 * no such document, and the two are deliberately distinct: `deriveAdvisoryStage`
 * gates on neither, so a project that predates S-02a is never gated and a
 * project whose first snapshot has not landed yet does not flash the company
 * screen before the goal review.
 */
export interface AdvisoryCompanyFactState {
  /** `undefined` = first snapshot outstanding, `null` = no fact for this project. */
  company: CompanyFact | null | undefined;
  /**
   * The counterparties, on the same terms. `null` for the nineteen engagement
   * types that have none, which is most projects.
   */
  counterparties: CounterpartiesFact | null | undefined;
  /**
   * Re-subscribes both listeners.
   *
   * Not needed to observe a write any more — that is what the listeners are for
   * — but a listener that has errored out stays dead, and this is the way back.
   * Callers that fire it after a turn settles are harmless: re-attaching
   * delivers the current document immediately.
   */
  refetch: () => void;
}

export function useAdvisoryCompanyFact(
  userId: string | undefined,
  projectId: string | undefined
): AdvisoryCompanyFactState {
  const [company, setCompany] = useState<CompanyFact | null | undefined>(undefined);
  const [counterparties, setCounterparties] = useState<CounterpartiesFact | null | undefined>(
    undefined
  );
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce(n => n + 1), []);

  useEffect(() => {
    if (!userId || !projectId) {
      setCompany(null);
      setCounterparties(null);
      return;
    }
    let alive = true;
    // Not reset to `undefined` on a re-subscribe: the screen is already
    // rendering the name, and blanking it mid-confirmation would flash the goal
    // review between the click and the answer.
    //
    // Both facts, on two independent channels: a denial or failure on one must
    // not deny the other, and they gate different stages.
    const unsubscribeCompany = listenToCompanyFact(
      userId,
      projectId,
      fact => {
        if (alive) {
          setCompany(fact);
        }
      },
      () => {
        // Best-effort, exactly like the server's own read: a failure here means
        // no company gate, never a broken engagement.
        if (alive) {
          setCompany(null);
        }
      }
    );
    const unsubscribeCounterparties = listenToCounterpartiesFact(
      userId,
      projectId,
      fact => {
        if (alive) {
          setCounterparties(fact);
        }
      },
      () => {
        if (alive) {
          setCounterparties(null);
        }
      }
    );
    return () => {
      alive = false;
      unsubscribeCompany();
      unsubscribeCounterparties();
    };
  }, [userId, projectId, nonce]);

  return { company, counterparties, refetch };
}

export default useAdvisoryCompanyFact;
